import { SocialPublishAtField } from '@app/modules/social-export/components/inputs/SocialPublishAtField';
import {
  getBaseExportSchema,
  MessageFormatter,
} from '@app/modules/social-export/components/validation/base-export-schema';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/useSocialExportFactory';
import { FormSection } from '@app/modules/social-export/screens/manage/platforms/FormSection';
import { zodResolver } from '@hookform/resolvers/zod';
import { socialExport } from '@mi-front/api-clients';
import { Grid, Stack } from '@mui/material';
import { useMemo } from 'react';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { tiktokSettingsSchema } from '../TikTokSettingsForm';
import { TiktokDisclosureSettings } from './disclosure/TiktokDisclosureSettings';
import { TikTokPublishFormActions } from './TikTokPublishFormActions';
import { TikTokSettingsFields } from './TiktokSettingsFields';
import { TikTokThumbFields } from './TikTokThumbFields';

const requiredMsg = { id: 'common.required', defaultMessage: 'Requerido' };

const disclosureRequiredMsg = {
  id: 'socialExport.tiktok.disclosure.selectAtLeastOne',
  defaultMessage: 'Indica si el contenido promociona tu marca, a un tercero o a ambos.',
};

function getTiktokPublishValidationSchema(t: MessageFormatter) {
  return getBaseExportSchema(t)
    .merge(tiktokSettingsSchema)
    .extend({
      title: z.string().trim().min(1, t(requiredMsg)),
      thumbType: z.enum(['DEFAULT', 'VIDEO_FRAME']),
      coverOffsetMs: z.number().nullable().optional(),
      discloseVideoContent: z.boolean().optional(),
      brandContent: z.boolean().optional(),
      brandOrganic: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.discloseVideoContent && !data.brandContent && !data.brandOrganic) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(disclosureRequiredMsg),
          path: ['brandOrganic'],
        });
      }
    });
}

type TikTokPublishValues = z.infer<ReturnType<typeof getTiktokPublishValidationSchema>>;

export type TikTokPublishFormContentProps = BaseExportFormProps<
  socialExport.TiktokPublishDto,
  socialExport.TiktokSettingsDto
> & {
  creatorInfo: socialExport.TikTokCreatorInfoDto;
};

/**
 * Inner TikTok publish form rendered after creator info has been loaded.
 * Receives creatorInfo from the parent so privacy and interaction options follow TikTok guidelines.
 */
export function TikTokPublishFormContent({
  fragment,
  onUpload,
  defaultSettings,
  videoUrl,
  creatorInfo,
}: TikTokPublishFormContentProps) {
  const { $t } = useIntl();
  const schema = useMemo(() => getTiktokPublishValidationSchema((msg) => $t(msg)), [$t]);
  const initialValues: TikTokPublishValues = {
    ...defaultSettings,
    title: fragment.title ?? '',
    thumbType: 'DEFAULT',
    coverOffsetMs: null,
    publishAt: null,
    privacyLevel: undefined!,
    allowComment: false,
    allowDuet: false,
    allowStitch: false,
    discloseVideoContent: false,
    brandContent: false,
    brandOrganic: false,
  } as TikTokPublishValues;

  const beforeSubmit = (data: TikTokPublishValues): socialExport.TiktokPublishDto => {
    const hasDisclosure = data.discloseVideoContent && (data.brandContent || data.brandOrganic);
    return {
      title: data.title,
      privacyLevel: data.privacyLevel as socialExport.TiktokPublishDtoPrivacyLevelEnumKey,
      allowComment: !data.allowComment,
      allowDuet: !data.allowDuet,
      allowStitch: !data.allowStitch,
      publishAt: data.publishAt || undefined,
      coverOffsetMs:
        data.thumbType === 'VIDEO_FRAME' && data.coverOffsetMs ? data.coverOffsetMs : undefined,
      ...(hasDisclosure && {
        brandContent: data.brandContent ?? false,
        brandOrganic: data.brandOrganic ?? false,
      }),
    } as socialExport.TiktokPublishDto;
  };

  return (
    <FormContainer
      values={initialValues}
      resolver={zodResolver(schema)}
      mode="onTouched"
      onSuccess={async (formData) => {
        await onUpload(beforeSubmit(formData));
      }}
    >
      <FormSection
        title={<FormattedMessage id="socialExport.form.content" defaultMessage="Contenido" />}
      >
        <Stack spacing={3}>
          <TextFieldElement
            name="title"
            label={<FormattedMessage id="socialExport.form.title" defaultMessage="Título" />}
            required
            fullWidth
            multiline
            rows={3}
          />
        </Stack>
      </FormSection>
      <FormSection
        title={<FormattedMessage id="socialExport.form.thumbnail" defaultMessage="Thumbnail" />}
      >
        <Grid container spacing={4}>
          <Grid item md={6}>
            <TikTokThumbFields videoUrl={videoUrl} />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection
        title={<FormattedMessage id="socialExport.form.settings" defaultMessage="Configuración" />}
      >
        <Stack spacing={2}>
          <TikTokSettingsFields creatorInfo={creatorInfo} />
          <TiktokDisclosureSettings />
        </Stack>
      </FormSection>
      <FormSection
        title={<FormattedMessage id="socialExport.form.schedule" defaultMessage="Programación" />}
      >
        <Grid container>
          <Grid item md={12}>
            <SocialPublishAtField />
          </Grid>
        </Grid>
      </FormSection>
      <TikTokPublishFormActions />
    </FormContainer>
  );
}
